/**
 * گریدِ ورودِ کارکرد — مثلِ دفترِ واقعیِ کارخانه.
 *
 * هر **ردیف** یک «کارِ کامل» است: یک تعداد که بینِ عملیات‌ها مشترک است. همان
 * سری که برش خورده، زیگزاگ و نوار و کش و کمرش زده می‌شود؛ پس تعداد یک بار در
 * ابتدای ردیف نوشته می‌شود و در هر ستون فقط **کارگرِ** آن عملیات انتخاب می‌شود.
 * تعدادِ هر عملیات در نرخِ خودش ضرب می‌شود و به همان کارگر می‌چسبد.
 *
 * ردیف‌ها آزادند چون کارِ واقعی این‌طور است: گاهی فقط رنگ یادداشت می‌شود، گاهی
 * یک کار چند روز طول می‌کشد و خردخرد وارد می‌شود، گاهی کنترل جلوتر از دوخت
 * می‌افتد. ستونِ «توضیحات» همان‌جا کار را مشخص می‌کند.
 *
 * ستون‌ها Hard-code نیستند و از نرخ‌نامهٔ همان محصول ساخته می‌شوند.
 *
 * 🔗 مدلِ تازه‌ای نمی‌سازد: هر کارگرِ انتخاب‌شده همان `ProductionEntry`ِ فعلی را
 * می‌سازد (با همان نرخ، Snapshot و محاسبهٔ کارمزد). فقط روشِ ورود جدولی شده.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Checkbox, Dropdown, Empty, Form, Input, InputNumber, Modal, Segmented, Select, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import Alert from "../../components/GxAlert";
import DraftBar from "../../components/DraftBar";
import { useDraft } from "../../utils/draft";
import { similarCatalogProducts } from "../../utils/productName";
import {
  CopyOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined, SaveOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import FormulaNumber from "../../components/FormulaNumber";
import useColumnLayout from "../../components/useColumnLayout";
import { autoId, loadOrder, saveOrder } from "../../utils/layoutStore";
import useFrequentPicks from "../../hooks/useFrequentPicks";
import { withFrequentFirst, countsFor } from "../../utils/frequentOptions";
import { filterByChildren } from "../../utils/selectFilter";
import gridKeyNav from "../../utils/gridKeyNav";
import { fmtNum } from "../../utils/dateUtils";
import { formatMoney } from "../../utils/money";
import "./OpsGrid.css";

const { Text } = Typography;

/** ستون‌هایی که عرضشان ثابت است؛ بقیه کش می‌آیند تا جدول در یک صفحه جا شود. */
const FIXED_W = { idx: 38, qty: 92, act: 66 };

/** بدونِ این‌ها برگه بی‌معنی است، پس در منویِ خاموش‌کردن نمی‌آیند. */
const ALWAYS_ON = new Set(["idx", "qty", "act"]);

/** عنوانِ خواندنیِ ستون برای منو (عنوانِ عملیات داخلِ Tooltip است) */
function colTitle(c) {
  if (typeof c.title === "string") return c.title;
  const inner = c.title?.props?.children;
  if (typeof inner === "string") return inner;
  if (inner?.props?.children) return String(inner.props.children);
  return String(c.key || "ستون");
}

let _seq = 0;
const newRow = (base = {}) => ({
  rowKey: `g${++_seq}-${Date.now()}`, quantity: null, notes: "", cells: {}, ...base,
});


/**
 * افزودنِ ستونِ عملیات — دو راه، چون دو موقعیتِ واقعی هست:
 *  • «از نرخ‌نامه»: عملیات وجود دارد ولی زیرِ محصولِ دیگری ثبت شده (یا مرحلهٔ
 *    ادغامی است). ستون همین‌جا اضافه می‌شود، نرخ‌نامه دست نمی‌خورد.
 *  • «عملیاتِ تازه»: اصلاً در نرخ‌نامه نیست. ردیفِ نرخ‌نامه ساخته می‌شود تا
 *    کارمزدش درست حساب شود؛ بعداً در «نرخ‌نامه» قابلِ اصلاح است.
 */
function AddOpModal({ open, onClose, allOperations, shownIds, productType, onPick, onCreate }) {
  const [mode, setMode] = useState("existing");
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();

  const options = useMemo(() => allOperations
    .filter((o) => !shownIds.has(o.value))
    .map((o) => ({ value: o.value, label: o.groupLabel || o.label })), [allOperations, shownIds]);

  const close = () => { setPick(null); form.resetFields(); onClose(); };

  const submit = async () => {
    if (mode === "existing") {
      if (!pick) return message.warning("یک عملیات انتخاب کنید");
      const op = allOperations.find((o) => o.value === pick);
      onPick(op);
      return close();
    }
    let v;
    try { v = await form.validateFields(); } catch { return undefined; }
    setBusy(true);
    try {
      const op = await onCreate({
        product_type: v.product_type,
        operation_name: v.operation_name,
        stitch_lines: v.stitch_lines,
        rate_per_line: v.rate_per_line,
      });
      if (op) onPick(op);
      close();
    } catch (e) {
      message.error(e?.response?.data?.detail || "افزودن عملیات ناموفق بود");
    }
    setBusy(false);
    return undefined;
  };

  return (
    <Modal open={open} onCancel={close} onOk={submit} confirmLoading={busy}
      title="افزودن ستونِ عملیات" okText="افزودن" cancelText="انصراف" destroyOnClose>
      <Segmented block style={{ marginBottom: 12 }} value={mode} onChange={setMode}
        options={[{ value: "existing", label: "از نرخ‌نامه" },
          { value: "new", label: "عملیاتِ تازه" }]} />

      {mode === "existing" ? (
        <>
          <Select showSearch style={{ width: "100%" }} optionFilterProp="label"
            placeholder="عملیات را جست‌وجو کنید" options={options}
            value={pick} onChange={setPick} />
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
            فقط یک ستونِ موقت به همین برگه اضافه می‌شود؛ نرخ‌نامه دست نمی‌خورد.
          </Text>
        </>
      ) : (
        <Form form={form} layout="vertical" initialValues={{ product_type: productType, stitch_lines: 1 }}>
          <Form.Item name="product_type" label="محصول" rules={[{ required: true, message: "محصول لازم است" }]}>
            <Input placeholder="مثلاً رکابی" />
          </Form.Item>
          <Form.Item name="operation_name" label="نام عملیات" rules={[{ required: true, message: "نام عملیات لازم است" }]}>
            <Input placeholder="مثلاً دمپا + کمر (ادغام)" />
          </Form.Item>
          <Space size={8}>
            <Form.Item name="stitch_lines" label="تعداد خط" rules={[{ required: true }]}>
              <InputNumber min={0.5} step={0.5} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="rate_per_line" label="فی هر خط (ریال)" rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: 180 }} />
            </Form.Item>
          </Space>
          <Alert type="info" showIcon
            message="این عملیات به نرخ‌نامه اضافه می‌شود"
            description="یعنی کارمزدش از همین حالا درست حساب می‌شود و دفعهٔ بعد خودش ستون دارد. اگر نرخ دقیق نیست، بعداً در صفحهٔ «نرخ‌نامه» اصلاحش کنید." />
        </Form>
      )}
    </Modal>
  );
}

export default function OpsGrid({
  operations,        // [{value: catalog_id, label, rate}] — نرخ‌نامهٔ همین محصول
  allOperations = [],// همهٔ عملیاتِ نرخ‌نامه (برای افزودنِ ستونِ دستی از محصولِ دیگر)
  productType,       // برای پیش‌پرکردنِ «عملیاتِ تازه»
  employees,         // [{value, label}]
  locked,            // قفلِ بعد از کنترل اول
  money,             // مجوزِ دیدنِ مبالغ
  onSubmit,          // async (entries[]) => void
  onUnlock,          // () => void
  onCreateOperation, // async ({product_type, operation_name, stitch_lines, rate_per_line}) => op
  // ── حالتِ ویرایش: ثبت‌های قبلی عیناً در همین گرید باز می‌شوند ──
  editRows,          // ردیف‌های بارگذاری‌شده از سرور (null = حالتِ ثبتِ عادی)
  onCancelEdit,      // بستنِ حالتِ ویرایش
  // ── محافظتِ خروج (v2.108.0): صفحهٔ محصول قبل از بستن می‌پرسد «ذخیره شود؟» ──
  onDirtyChange,     // (bool) => void — پیش‌نویسِ ذخیره‌نشده دارد؟
  submitRef,         // ref — صفحهٔ محصول با `submitRef.current()` همین را ذخیره می‌کند
  // ── پیش‌نویسِ خودکار (v2.142.0): برگهٔ نیمه‌کاره با بروزرسانی/بستنِ صفحه از بین نرود ──
  draftKey,          // مثلاً "ops:<کد تولید>:<سایز>" — خالی = بدونِ پیش‌نویس
}) {
  const [rows, setRows] = useState([newRow()]);
  const editing = !!editRows;
  /* «پرتکرار» — کارِ کارخانه تکرارشونده است: همان چند نفر همان چند عملیات را
     می‌زنند. کسی که در سالِ جاری بیش از دو بار برای این عملیات ثبت شده، بالای
     فهرست می‌آید تا نیازی به گشتن بینِ صد نفر نباشد. هیچ‌کس حذف نمی‌شود. */
  const freq = useFrequentPicks();
  const empOptionsFor = useCallback((catalogId) => {
    const counts = countsFor(freq.employees_by_operation, catalogId);
    if (!counts) return employees;
    return withFrequentFirst(employees, counts, { showCount: true });
  }, [freq, employees]);
  // با بازشدنِ ویرایش، پیش‌نویسِ فعلی کنار می‌رود و ثبت‌های واقعی می‌نشینند؛
  // با بستنش برگه دوباره تمیز می‌شود.
  useEffect(() => {
    setRows(editRows && editRows.length ? editRows : [newRow()]);
    // ستونِ عملیاتی که در نرخ‌نامهٔ این محصول نیست ولی ثبتِ قدیمی دارد، باید
    // خودش اضافه شود؛ وگرنه سلولش دیده نمی‌شود و «ذخیره» حذفش می‌کند.
    if (editRows && editRows.length) {
      const have = new Set(operations.map((o) => o.value));
      const used = new Set();
      editRows.forEach((r) => Object.keys(r.cells || {}).forEach((k) => used.add(Number(k))));
      const missing = [...used].filter((id) => !have.has(id))
        .map((id) => allOperations.find((o) => o.value === id))
        .filter(Boolean);
      if (missing.length) setExtraOps((prev) => {
        const seen = new Set(prev.map((o) => o.value));
        return [...prev, ...missing.filter((o) => !seen.has(o.value))];
      });
    }
  }, [editRows]);   // eslint-disable-line
  const [saving, setSaving] = useState(false);
  // ستون‌هایی که کاربر همین‌جا دستی اضافه کرده — مثلاً یک مرحلهٔ دوختِ اضافه که
  // هنوز در نرخ‌نامهٔ این محصول نیست. تا وقتی نرخ‌نامه اصلاح شود، همین کافی است.
  const [extraOps, setExtraOps] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  /* حافظهٔ «عملیاتی که قبلاً برای این محصول انتخاب کرده بودیم».
     چرا per **محصول** و نه per پارت: خودِ فهرستِ عملیات از نرخ‌نامهٔ همان
     محصول می‌آید و همهٔ سایزهای یک سفارش دقیقاً یک ستونِ یکسان دارند؛ پس
     «آستین‌دار» یک‌بار تنظیم می‌شود و برای هر بیجکِ آستین‌دارِ بعدی می‌ماند.
     نامِ محصول متنِ آزاد است، پس نرمال می‌شود تا «ی/ک»ِ عربی و نیم‌فاصله
     دو سطلِ جدا نسازند. */
  const memKey = useMemo(() => {
    const t = String(productType || "").trim()
      .replace(/\u064a/g, "\u06cc").replace(/\u0643/g, "\u06a9")
      .replace(/\u200c/g, " ").replace(/\s+/g, " ");
    return t ? `opset:${t}` : null;
  }, [productType]);

  /* ⚠️ گرید با هر بار عوض‌شدنِ سایز remount می‌شود (key={activePageKey} در
     BijackDrawer)، پس `extraOps` هر بار خالی می‌شد. اینجا از حافظه برمی‌گردد.

     🔴 دو تلهٔ ظریف که هر دو گرفته شده‌اند:
     ۱) `operations` در BijackDrawer با `ratesOf(activeOrder)` **درجا** ساخته
        می‌شود، یعنی هر رندر یک آرایهٔ تازه؛ پس این افکت هر رندر اجرا می‌شود.
        اگر `setExtraOps` بی‌قید آرایهٔ تازه برگرداند، رندرِ بعدی را می‌سازد و
        حلقهٔ بی‌پایان درست می‌شود. برای همین وقتی چیزی برای افزودن نیست،
        **عیناً `prev`** برمی‌گردد.
     ۲) بازیابی فقط **یک‌بار per محصول** انجام می‌شود؛ وگرنه ستونی که کاربر
        عمداً بسته، لحظه‌ای بعد دوباره باز می‌شد. */
  const restoredFor = useRef(null);
  /* اگر محصولِ همین گرید عوض شود (بدونِ remount)، ستون‌های دستیِ محصولِ قبلی
     نباید بمانند — وگرنه زیرِ کلیدِ محصولِ تازه ذخیره می‌شوند و از این به بعد
     یک عملیاتِ بی‌ربط روی آن محصول ظاهر می‌شود. */
  useEffect(() => { setExtraOps([]); restoredFor.current = null; }, [memKey]);
  useEffect(() => {
    if (!memKey || restoredFor.current === memKey) return;
    // تا نرخ‌نامه نیامده صبر کن — بازیابیِ زودهنگام یعنی «چیزی پیدا نشد»
    if (!allOperations.length) return;
    restoredFor.current = memKey;
    const saved = loadOrder("pick", memKey);
    const ids = Array.isArray(saved?.extra) ? saved.extra : [];
    if (!ids.length) return;
    const have = new Set(operations.map((o) => o.value));
    // شناسه‌ای که دیگر در نرخ‌نامه نیست (غیرفعال شده) نباید ستونِ بدونِ نرخ
    // بسازد — `rateOf` صفر برمی‌گرداند و جمعِ کارمزد بی‌صدا کم می‌شود.
    const resolved = ids
      .filter((id) => !have.has(id))
      .map((id) => allOperations.find((o) => o.value === id))
      .filter(Boolean);
    if (!resolved.length) return;
    setExtraOps((prev) => {
      const seen = new Set(prev.map((o) => o.value));
      const add = resolved.filter((o) => !seen.has(o.value));
      return add.length ? [...prev, ...add] : prev;   // ← بدونِ این، حلقهٔ رندر
    });
  }, [memKey, operations, allOperations]);

  /* ذخیره فقط **بعد از** بازیابی. وگرنه اولین رندر (که `extraOps` خالی است)
     حافظهٔ ذخیره‌شده را با یک آرایهٔ خالی بازنویسی می‌کرد — یعنی قابلیت
     خودش را پاک می‌کرد. */
  useEffect(() => {
    if (!memKey || restoredFor.current !== memKey) return;
    saveOrder("pick", memKey, { extra: extraOps.map((o) => o.value) });
  }, [memKey, extraOps]);

  // ستون‌های مؤثر = نرخ‌نامهٔ محصول + ستون‌های دستی (بدونِ تکرار)
  const allCols = useMemo(() => {
    const seen = new Set(operations.map((o) => o.value));
    return [...operations, ...extraOps.filter((o) => !seen.has(o.value))];
  }, [operations, extraOps]);

  /* چیدمان per **محصول**.
     🐞 قبلاً کلید هشِ فهرستِ عملیات بود؛ یعنی به‌محضِ اینکه یک عملیات به
     نرخ‌نامهٔ آن محصول اضافه یا غیرفعال می‌شد، کلید عوض می‌شد و کلِ حافظهٔ
     ستون‌ها از صفر شروع می‌کرد. حالا کلید خودِ نامِ محصول است و حافظه می‌ماند
     (شناسه‌های مرده هنگامِ تغییرِ ستون‌ها هرس می‌شوند، نه پاک). */
  const layoutId = useMemo(
    () => (memKey ? `opsgrid-${memKey}` : `opsgrid-${autoId(operations.map((o) => String(o.value)))}`),
    [memKey, operations]);

  const patchRow = (rowKey, patch) => setRows((prev) => prev.map((r) => (
    r.rowKey === rowKey ? { ...r, ...patch } : r)));

  const setWorker = (rowKey, catId, employee_id) => setRows((prev) => prev.map((r) => (
    r.rowKey === rowKey ? { ...r, cells: { ...r.cells, [catId]: employee_id || null } } : r)));

  /** هر تخصیصِ معتبر: ردیفی که تعداد دارد × عملیاتی که کارگرش انتخاب شده */
  const filled = useMemo(() => {
    const out = [];
    rows.forEach((r) => {
      const qty = Number(r.quantity) || 0;
      if (qty <= 0) return;
      Object.entries(r.cells).forEach(([catId, employee_id]) => {
        if (employee_id) out.push({ row: r, catalog_id: Number(catId), employee_id, quantity: qty });
      });
    });
    return out;
  }, [rows]);

  const rateOf = (catalog_id) =>
    Number(allCols.find((o) => o.value === catalog_id)?.rate?.total_rate) || 0;

  // ── جمع‌ها (زنده، از همین پیش‌نویس) ──
  const perOp = useMemo(() => {
    const m = {};
    filled.forEach((f) => { m[f.catalog_id] = (m[f.catalog_id] || 0) + f.quantity; });
    return m;
  }, [filled]);

  const perEmployee = useMemo(() => {
    const m = new Map();
    filled.forEach((f) => {
      const cur = m.get(f.employee_id) || { qty: 0, amount: 0 };
      m.set(f.employee_id, {
        qty: cur.qty + f.quantity,
        amount: cur.amount + rateOf(f.catalog_id) * f.quantity,
      });
    });
    return [...m.entries()].map(([id, v]) => ({
      id, name: employees.find((e) => e.value === id)?.label || `#${id}`, ...v,
    })).sort((a, b) => b.qty - a.qty);
  }, [filled, employees, allCols]);

  /** تعدادِ خودِ کار (نه جمعِ تخصیص‌ها) — همان چیزی که برش خورده */
  const totalPieces = rows.reduce((a, r) => a + (Number(r.quantity) || 0), 0);
  const grandAmount = filled.reduce((a, f) => a + rateOf(f.catalog_id) * f.quantity, 0);

  const submit = async () => {
    if (!filled.length) { message.warning("هیچ کارگری با تعداد ثبت نشده است"); return false; }
    setSaving(true);
    let ok = true;
    try {
      await onSubmit(filled.map((f) => ({
        employee_id: f.employee_id,
        catalog_id: f.catalog_id,
        quantity: f.quantity,
        notes: f.row.notes || null,
        // ردیفِ گرید — تا سرور بتواند بعداً همین چیدمان را عیناً برگرداند
        row_uid: f.row.rowKey,
      })), { editing, gridCatalogIds: allCols.map((o) => o.value) });
      if (!editing) { setRows([newRow()]); draft.clear(); }   // در ویرایش، صداکننده refresh می‌کند
    } catch {
      /* پیام خطا را صداکننده نشان می‌دهد؛ پیش‌نویس دست‌نخورده می‌ماند */
      ok = false;
    }
    setSaving(false);
    return ok;
  };
  if (submitRef) submitRef.current = submit;

  /* «دست‌خورده» = هر تعداد/کارگر/توضیحی در برگه، یا حالتِ ویرایشِ ثبت‌های قبلی */
  const hasWork = (rs) => (rs || []).some((r) => r.quantity || (r.notes || "").trim()
    || Object.values(r.cells || {}).some(Boolean));
  const dirty = editing || hasWork(rows);
  const draft = useDraft(draftKey, rows, { enabled: !!draftKey && !editing && !locked, isEmpty: (rs) => !hasWork(rs) });
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty]);   // eslint-disable-line
  useEffect(() => () => onDirtyChange?.(false), []);        // eslint-disable-line

  const baseColumns = [
    {
      key: "idx", title: "#", align: "center",
      render: (_, __, i) => <Text type="secondary">{fmtNum(i + 1)}</Text>,
      gxSummary: () => <b>جمع</b>,
    },
    {
      key: "qty", title: "تعداد",
      render: (_, r) => (
        <FormulaNumber size="small" min={1} placeholder="تعداد" value={r.quantity ?? null}
          disabled={locked} onChange={(v) => patchRow(r.rowKey, { quantity: v })} />
      ),
      gxSummary: () => <b>{fmtNum(totalPieces)}</b>,
    },
    {
      // خواستهٔ کاربر: دکمه‌های حذف/تکرار جای ثابت، درست بعدِ تعداد — نه تهِ
      // یک جدولِ پهن که برای رسیدن بهشان باید اسکرول کرد.
      key: "act", title: "",
      render: (_, r) => (
        <Space size={2}>
          <Tooltip title="تکرارِ ردیف (کارگرها می‌مانند، تعداد خالی)">
            <Button size="small" icon={<CopyOutlined />} disabled={locked}
              onClick={() => setRows((prev) => {
                const i = prev.findIndex((x) => x.rowKey === r.rowKey);
                const copy = newRow({ notes: r.notes, cells: { ...r.cells } });
                const next = [...prev];
                next.splice(i + 1, 0, copy);
                return next;
              })} />
          </Tooltip>
          <Button size="small" danger icon={<DeleteOutlined />}
            disabled={locked || rows.length === 1}
            onClick={() => setRows((prev) => prev.filter((x) => x.rowKey !== r.rowKey))} />
        </Space>
      ),
    },
    ...allCols.map((op) => ({
      key: `op:${op.value}`,
      gxSummary: () => (perOp[op.value] ? <b>{fmtNum(perOp[op.value])}</b> : "—"),
      title: (
        <Tooltip title={money && op.rate?.total_rate
          ? `فی هر واحد: ${formatMoney(op.rate.total_rate)} ریال` : undefined}>
          <span>{op.label}</span>
        </Tooltip>
      ),
      render: (_, r) => {
        const emp = r.cells[op.value] || null;
        const qty = Number(r.quantity) || 0;
        return (
          <Space direction="vertical" size={0} style={{ width: "100%" }}>
            <Select showSearch allowClear size="small" style={{ width: "100%" }}
              placeholder="کارگر"
              /* `optionFilterProp` روی گزینه‌های گروه‌بندی‌شده هدرِ گروه را هم
                 تست می‌کند و کلِ گروه را بی‌فیلتر نشان می‌دهد؛ این تابع گروه را
                 هیچ‌وقت تطبیق نمی‌دهد. */
              filterOption={filterByChildren}
              options={empOptionsFor(op.value)}
              // شمارشِ دفعات فقط داخلِ دراپ‌داون دیده می‌شود؛ `label` دست‌نخورده
              // می‌ماند تا جست‌وجو و متنِ انتخاب‌شده عوض نشود.
              optionRender={(o) => (o.data?.gxCount ? (
                <Space size={4} style={{ justifyContent: "space-between", width: "100%" }}>
                  <span>{o.data.label}</span>
                  <Text type="secondary" style={{ fontSize: 11 }}>{fmtNum(o.data.gxCount)} بار</Text>
                </Space>
              ) : o.data?.label)}
              value={emp} disabled={locked}
              onChange={(v) => setWorker(r.rowKey, op.value, v)} />
            {/* وقتی کارگر انتخاب شد، همان‌جا معلوم است چقدر برایش می‌خورد */}
            {emp && qty > 0 && money && op.rate?.total_rate ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {formatMoney(rateOf(op.value) * qty)}
              </Text>
            ) : null}
          </Space>
        );
      },
    })),
    {
      key: "notes", title: "توضیحات",
      render: (_, r) => (
        <Input size="small" placeholder="رنگ / سری / توضیح" value={r.notes} disabled={locked}
          onChange={(e) => patchRow(r.rowKey, { notes: e.target.value })} />
      ),
    },
  ];

  // چیدمانِ ستون‌ها per user + جاساز کردن در عرضِ موجود (بدونِ اسکرولِ چپ‌وراست)
  const {
    columns, components, wrapRef, reset, compact,
    allColumns, hidden, toggleHidden, showAll, cid,
  } = useColumnLayout(layoutId, baseColumns, {
    fit: { fixed: FIXED_W, minWidth: 92, maxWidth: 190, compactBelow: 128 },
    // این سه ستون بدونشان برگه بی‌معنی است، پس خاموش‌شدنی نیستند
    alwaysOn: ["idx", "qty", "act"],
  });

  const hiddenCount = [...hidden].filter((h) => !ALWAYS_ON.has(h)).length;

  // اگر ستونی خاموش شود ولی در پیش‌نویس داده داشته باشد، آن داده همچنان ثبت
  // می‌شود — پس باید دیده شود، وگرنه کاربر چیزی می‌فرستد که نمی‌بیند.
  const hiddenWithData = useMemo(() => {
    const visible = new Set(columns.map((c) => c.key));
    const ids = new Set(filled.map((f) => `op:${f.catalog_id}`));
    return allCols.filter((op) => ids.has(`op:${op.value}`)
      && !visible.has(`op:${op.value}`));
  }, [columns, filled, allCols]);

  /** منویِ تنظیماتِ ستون‌ها — روشن/خاموش، افزودن، بازگشت به پیش‌فرض. */
  const columnMenu = {
    items: [
      { key: "__title", disabled: true,
        label: <Text type="secondary" style={{ fontSize: 11 }}>کدام ستون‌ها دیده شوند؟</Text> },
      ...allColumns
        .filter((c) => !ALWAYS_ON.has(cid(c)))
        .map((c) => ({
          key: cid(c),
          label: (
            <Checkbox checked={!hidden.has(cid(c))} onClick={(e) => e.stopPropagation()}
              onChange={(e) => toggleHidden(cid(c), e.target.checked)}>
              {colTitle(c)}
            </Checkbox>
          ),
        })),
      { type: "divider" },
      { key: "__all", label: <span><ReloadOutlined /> نمایشِ همه</span> },
      { key: "__add", label: <span><PlusOutlined /> افزودنِ ستونِ عملیات…</span> },
      { key: "__reset", label: <span><SettingOutlined /> بازگشت به چیدمانِ پیش‌فرض</span> },
    ],
    onClick: ({ key }) => {
      if (key === "__all") showAll();
      if (key === "__add") setAddOpen(true);
      if (key === "__reset") reset();
    },
  };

  if (!operations.length) {
    // نام‌های نرخ‌نامه که شبیهِ این محصول‌اند — تا کاربر بفهمد مشکل «املا/فاصله» است نه نبودِ نرخ
    const similar = similarCatalogProducts((allOperations || []).map((o) => o.rate).filter(Boolean),
      { product_type: productType });
    return (
      <Alert type="warning" showIcon
        message={`برای «${productType || "این محصول"}» عملیاتی در نرخ‌نامه پیدا نشد`}
        description={similar.length
          ? `در نرخ‌نامه محصول(های) مشابه هست: ${similar.map((n) => `«${n}»`).join("، ")}. نامِ محصولِ این پارت را در ثبتِ برش با همان نام یکی کنید، یا در «نرخ‌نامه» عملیات‌ها را زیرِ همین نام تعریف کنید.`
          : "اول در «نرخ‌نامه» عملیات‌های این محصول را اضافه کنید تا ستون‌های جدول ساخته شوند."} />
    );
  }

  return (
    <>
    {!editing && (
      <DraftBar draft={draft} what="برگهٔ ثبتِ کارکردِ این سایز"
        onRestore={(v) => { if (Array.isArray(v) && v.length) setRows(v); }} />
    )}
    <Card size="small" style={{ marginBottom: 12 }}
      title={editing
        ? "ویرایشِ ثبت‌های قبلی — همان برگه، همان ردیف‌ها"
        : "ثبت کارکرد (تعدادِ ردیف مشترک است — در هر ستون فقط کارگر)"}
      extra={locked ? (
        <Space>
          <Tag color="purple">کنترل اول شده — قفل</Tag>
          <Button size="small" onClick={onUnlock}>باز کردن برای اصلاح</Button>
        </Space>
      ) : (
        <Space size={4}>
          <Tooltip title="ستون‌های اضافه را خاموش کنید تا برگه شلوغ نشود · سرستون را بکشید = جابه‌جایی · لبه = عرض. همه برای خودتان ذخیره می‌شود.">
            <Dropdown menu={columnMenu} trigger={["click"]}>
              <Button size="small" icon={<SettingOutlined />}>
                ستون‌ها{hiddenCount ? ` (${fmtNum(hiddenCount)} خاموش)` : ""}
              </Button>
            </Dropdown>
          </Tooltip>
          <Button icon={<PlusOutlined />} size="small"
            onClick={() => setRows((prev) => [...prev, newRow()])}>افزودن ردیف</Button>
        </Space>
      )}>
      {hiddenWithData.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 10 }}
          message="ستونِ خاموش با اطلاعاتِ ثبت‌نشده"
          description={`${hiddenWithData.map((o) => o.label).join("، ")} خاموش است ولی داده دارد — با «ثبت کارکردها» همان هم ذخیره می‌شود. اگر نمی‌خواهید، از «ستون‌ها» روشنش کنید و پاکش کنید.`} />
      )}
      {locked && (
        <Alert type="info" showIcon style={{ marginBottom: 10 }}
          message="این ردیف کنترل اول شده و دوختش قفل است"
          description="برای اصلاح، «باز کردن برای اصلاح» را بزنید؛ کنترل اول حذف می‌شود و کارکردها دست‌نخورده می‌مانند. بعد از اصلاح دوباره کنترل اول را ثبت کنید." />
      )}

      <AddOpModal open={addOpen} onClose={() => setAddOpen(false)}
        allOperations={allOperations} productType={productType}
        shownIds={new Set(allCols.map((o) => o.value))}
        onPick={(op) => op && setExtraOps((prev) => (
          prev.some((x) => x.value === op.value) ? prev : [...prev, op]))}
        onCreate={onCreateOperation} />

      {/* ref روی wrapper: هم عرضِ موجود را می‌سنجد هم auto-fitِ دابل‌کلیک */}
      {/* Tab/فلش‌ها بینِ سلول‌ها حرکت می‌کنند — Tab عمداً پایین می‌رود */}
      <div ref={wrapRef} onKeyDown={gridKeyNav}
        className={compact ? "ops-grid ops-grid--compact" : "ops-grid"}>
              {/* انتخابگرِ سریعِ ستون‌ها — خواستهٔ کاربر: همان لحظهٔ ورود معلوم باشد
          کدام عملیات‌ها فعال‌اند و با یک تیک روشن/خاموش شوند. همان حافظهٔ
          per-user منوی «ستون‌ها» است، فقط جلوی چشم. */}
      <div style={{ marginBottom: 8, display: "flex", flexWrap: "wrap", gap: 4,
        alignItems: "center" }}>
        <Text type="secondary" style={{ fontSize: 12 }}>عملیات‌های این برگه:</Text>
        {allColumns.filter((c) => String(cid(c)).startsWith("op:")).map((c) => {
          const id = cid(c);
          const on = !hidden.has(id);
          return (
            <Tag.CheckableTag key={id} checked={on}
              onChange={(v) => toggleHidden(id, v)}
              style={{ border: "1px solid var(--border-strong,#d9d9d9)",
                userSelect: "none" }}>
              {colTitle(c)}
            </Tag.CheckableTag>
          );
        })}
      </div>
      <Table size="small" rowKey="rowKey" dataSource={rows} columns={columns}
          components={components} tableLayout="fixed"
          pagination={false} scroll={{ x: "max-content" }}
          locale={{ emptyText: <Empty description="ردیفی نیست" /> }}
          summary={() => (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row>
                {/* جمع‌ها از خودِ ستون‌ها می‌آیند تا با جابه‌جاییِ ستون هم‌راستا بمانند */}
                {columns.map((c, i) => (
                  <Table.Summary.Cell key={c.key || i} index={i} align="center">
                    {typeof c.gxSummary === "function" ? c.gxSummary() : null}
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          )} />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        marginTop: 10, gap: 12, flexWrap: "wrap" }}>
        <Space direction="vertical" size={2}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {filled.length
              ? <>{fmtNum(totalPieces)} عدد کار · {fmtNum(filled.length)} تخصیص به کارگرها
                  {money && <> · کارمزد <b style={{ color: "#107C41" }}>{formatMoney(grandAmount)} ریال</b></>}</>
              : "تعدادِ ردیف را یک بار بنویسید (می‌تواند جمع باشد: ۱۲۰+۸۰) و بعد در هر ستون کارگرش را انتخاب کنید"}
          </Text>
          {/* جمعِ کارِ هر پرسنل — همان چیزی که سرِ ماه لازم می‌شود */}
          {perEmployee.length > 0 && (
            <Space size={4} wrap>
              {perEmployee.map((e) => (
                <Tag key={e.id}>{e.name}: {fmtNum(e.qty)}
                  {money && ` · ${formatMoney(e.amount)}`}</Tag>
              ))}
            </Space>
          )}
        </Space>
        <Space>
          {editing && (
            <Button onClick={onCancelEdit} disabled={saving}>انصراف از ویرایش</Button>
          )}
          <Button type="primary" icon={<SaveOutlined />} loading={saving}
            disabled={locked || !filled.length} onClick={submit}
            style={{ background: "#107C41", borderColor: "#107C41" }}>
            {editing ? "ذخیرهٔ تغییرات" : "ثبت کارکردها"}
          </Button>
        </Space>
      </div>
    </Card>
    </>
  );
}
